// conflictQueue.js — the queue a human actually sees (invariant 6, audit C1/finding 4).
//
// WHY THIS SCREEN EXISTS AT ALL.
//
// The audit's opening finding is that `sync_status:'conflict'` was "written once and
// never read anywhere in the codebase" — a tombstone in IndexedDB on one cashier's
// device. `updatePendingCountUI()` counted only `'pending'`, so the badge returned to
// zero: cash taken, receipt printed, sale existing nowhere, and no screen that could
// ever tell anybody.
//
// Invariant 6 says a parked event must (a) reach a human AND (b) have a defined path
// back into the ledger. The engine now delivers (a) as a count on a banner; this
// module is the rest of both halves — the list, the reason in French, and the two
// buttons that decide what happens to the money.
//
// ── THREE RULES THIS FILE LIVES UNDER ───────────────────────────────────────
//
//  1. REINJECT REPLAYS THE ORIGINAL UUID. `resolve_conflict` re-runs the parked
//     event exactly as it was, under its ORIGINAL caller, so the role context matches
//     the first submission and idempotency still holds (invariant 2, harness F14). A
//     re-run of an event that did land heals to `already_present`. Nothing here mints
//     a new id, and nothing here rewrites the payload.
//
//  2. A RE-CONFLICT IS NOT A DEAD END. When a reinject is refused again the server
//     leaves the row `open` and APPENDS the new reason. That state is rendered as
//     what it is — still in the queue, with the newest refusal named — never as a
//     failure the admin can do nothing about, and never as a resolution.
//
//  3. THE COUNT COUNTS `status === 'open'` AND NOTHING ELSE. The pull re-imports
//     `reinjected` and `rejected` rows every cycle; counting them keeps « {n}
//     enregistrement(s) nécessitent votre attention » lit forever after the admin
//     resolved everything, and a banner that never clears is a banner nobody reads.
//     Alarm fatigue kills the alarm, which is the C1 failure with extra steps.
//
// Money parked in a conflict is money the hospital has already taken, so the count is
// published to the DASHBOARD, not only to this screen: an admin who never opens the
// modal must still be unable to miss it.
//
// EVERY READ PAGINATES (spec §8, audit C6) and every refusal is rendered through
// `messageFor()` (spec §9), so no screen invents its own wording for a rule that fired
// in Postgres.

import { supabase } from './supabaseClient.js';
import { showStatus, formatCurrency, formatDateTime } from './utils.js';

import { db } from './db/schema.js';
import { fetchAllPages } from './db/index.js';
import { getEngine } from './ui/engine-boot.js';
import { messageFor, messageForError, conflictQueueNotice } from './ui/engine-messages.js';
import { formatReceiptNo } from './money/index.js';

/**
 * Escape text destined for an `innerHTML` template (audit M7). A parked event carries
 * a PATIENT NAME typed at a cashier's desk and renders here, in the admin's session —
 * the one account that may void a receipt. This is the highest-value escape in the
 * application, not a formality.
 * @param {*} value
 * @returns {string}
 */
function escapeHtml(value) {
    return String(value == null ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

/** How often the dashboard count is re-derived from the tables. */
const COUNT_REFRESH_MS = 30000;

/** The separator `resolve_conflict` uses when it appends a failed reinject's reason. */
const REASON_SEPARATOR = ' | ';

/** The prefix that migration 0005 puts in front of an appended reinject failure. */
const RETRY_PREFIX = 'reinject-failed:';

// ---------------------------------------------------------------------------
// Pure logic — no DOM, no network. Unit-tested in conflictQueue.test.js.
// ---------------------------------------------------------------------------

/**
 * @typedef {Object} QueueRow
 * @property {string} eventId The parked event's own UUID (invariant 2).
 * @property {string|null} conflictId The `conflicts` table primary key, or null when
 *   this device knows of the park but has not yet seen the server's row.
 * @property {string|null} deviceId Denormalized device, deliberately not an FK.
 * @property {'open'|'reinjected'|'rejected'} status
 * @property {string} reason Raw reason string, retries included.
 * @property {string|null} raisedAt ISO instant the event parked.
 * @property {object|null} event The full original event, UUID included.
 * @property {'server'|'device'|'both'} source Where we learned about it.
 */

/**
 * The event id a conflict row is about.
 *
 * A server row's primary key is the CONFLICT's id; the event's id lives inside
 * `ledger_event`. The local mirror is keyed by the EVENT id already (`db/sync.js`
 * writes it that way so the push-ack row and the pulled row converge on one row
 * rather than two). Keying the merge on anything else shows one parked sale twice.
 *
 * @param {object} row
 * @returns {string|null}
 */
export function eventIdOf(row) {
    if (!row || typeof row !== 'object') return null;
    const fromEvent = row.ledger_event && row.ledger_event.id;
    if (fromEvent != null && fromEvent !== '') return String(fromEvent);
    if (row.id != null && row.id !== '') return String(row.id);
    return null;
}

/**
 * Normalize one row of the LOCAL mirror (`db/schema.js` `conflicts` store).
 * @param {object} row
 * @returns {QueueRow|null}
 */
export function fromLocalRow(row) {
    const eventId = eventIdOf(row);
    if (eventId == null) return null;
    return {
        eventId,
        conflictId: row.server_conflict_id != null ? String(row.server_conflict_id) : null,
        deviceId: row.device_id != null ? String(row.device_id) : null,
        status: normalizeStatus(row.status),
        reason: typeof row.reason === 'string' ? row.reason : '',
        raisedAt: row.raised_at != null ? String(row.raised_at) : null,
        event: row.ledger_event || null,
        source: 'device'
    };
}

/**
 * Normalize one row of the SERVER `conflicts` table.
 * @param {object} row
 * @returns {QueueRow|null}
 */
export function fromServerRow(row) {
    const eventId = eventIdOf(row);
    if (eventId == null) return null;
    return {
        eventId,
        conflictId: row.id != null ? String(row.id) : null,
        deviceId: row.device_id != null ? String(row.device_id) : null,
        status: normalizeStatus(row.status),
        reason: typeof row.reason === 'string' ? row.reason : '',
        raisedAt: row.raised_at != null ? String(row.raised_at) : null,
        event: row.ledger_event || null,
        source: 'server'
    };
}

/**
 * An unrecognised status is treated as `open`, never dropped.
 *
 * The whole failure this queue exists to fix is a status nobody read. A row whose
 * status we cannot classify is a row that still needs a human, so it stays in the
 * queue rather than vanishing from it.
 * @param {*} status
 * @returns {'open'|'reinjected'|'rejected'}
 */
function normalizeStatus(status) {
    return status === 'reinjected' || status === 'rejected' ? status : 'open';
}

/**
 * Merge the local mirror and the server table into one list, newest first.
 *
 * The SERVER wins on `status` and `reason` — it is the authority, and it is where a
 * reinject's appended reason lands. The LOCAL row still contributes: a device that
 * parked an event whose server row this admin's read did not return (offline, or a
 * conflict raised against a device row an RLS policy hides) must still be visible.
 * Rows present in both keep the server's verdict and are marked `both`, so the screen
 * can say which parks this browser is holding a mirror of.
 *
 * @param {object[]} localRows Raw rows from the Dexie `conflicts` store.
 * @param {object[]} serverRows Raw rows from `public.conflicts`.
 * @returns {QueueRow[]} Newest first; ties broken on the event id so the order is
 *   stable across renders (a list that reshuffles under a cursor is a misclick).
 */
export function mergeConflicts(localRows, serverRows) {
    /** @type {Map<string, QueueRow>} */
    const byEvent = new Map();

    for (const raw of Array.isArray(localRows) ? localRows : []) {
        const row = fromLocalRow(raw);
        if (row) byEvent.set(row.eventId, row);
    }

    for (const raw of Array.isArray(serverRows) ? serverRows : []) {
        const row = fromServerRow(raw);
        if (!row) continue;
        const local = byEvent.get(row.eventId);
        byEvent.set(row.eventId, local
            ? {
                ...row,
                // Keep whatever identity either side knows; a local row that has been
                // pulled already carries `server_conflict_id`.
                conflictId: row.conflictId || local.conflictId,
                deviceId: row.deviceId || local.deviceId,
                raisedAt: row.raisedAt || local.raisedAt,
                event: row.event || local.event,
                source: 'both'
            }
            : row);
    }

    return [...byEvent.values()].sort(compareNewestFirst);
}

/**
 * @param {QueueRow} a
 * @param {QueueRow} b
 * @returns {number}
 */
function compareNewestFirst(a, b) {
    const at = a.raisedAt == null ? -Infinity : Date.parse(a.raisedAt);
    const bt = b.raisedAt == null ? -Infinity : Date.parse(b.raisedAt);
    const an = Number.isNaN(at) ? -Infinity : at;
    const bn = Number.isNaN(bt) ? -Infinity : bt;
    if (an !== bn) return bn - an;
    return a.eventId < b.eventId ? -1 : a.eventId > b.eventId ? 1 : 0;
}

/**
 * The rows that still need a decision.
 * @param {QueueRow[]} rows
 * @returns {QueueRow[]}
 */
export function openConflicts(rows) {
    return (Array.isArray(rows) ? rows : []).filter(r => r && r.status === 'open');
}

/**
 * The number on the banner. `open` ONLY — see rule 3 in the module header.
 * @param {QueueRow[]} rows
 * @returns {number}
 */
export function openConflictCount(rows) {
    return openConflicts(rows).length;
}

/**
 * @typedef {Object} ReasonSegment
 * @property {string} code The kebab-case verdict.
 * @property {string} message The French sentence for it (spec §9).
 * @property {boolean} isRetry True when this refusal came from a failed REINJECT
 *   rather than from the original submission.
 */

/**
 * Split a conflict's reason into its segments, newest last.
 *
 * `resolve_conflict` appends ` | reinject-failed: <code>` on every refused reinject,
 * so the column accumulates the whole history of what this event has been refused
 * for. Rendering only the first segment would hide the reason the admin most needs —
 * the one from the attempt they just made.
 *
 * @param {string} reason
 * @returns {ReasonSegment[]}
 */
export function reasonSegments(reason) {
    const raw = typeof reason === 'string' ? reason : '';
    const parts = raw.split(REASON_SEPARATOR).map(p => p.trim()).filter(p => p !== '');
    if (parts.length === 0) {
        // No reason at all is itself a verdict `messageFor` has copy for: the server
        // refused and said nothing. It is never rendered as a blank cell.
        return [{ code: '', message: messageFor(null), isRetry: false }];
    }
    return parts.map(part => {
        const isRetry = part.startsWith(RETRY_PREFIX);
        const code = isRetry ? part.slice(RETRY_PREFIX.length).trim() : part;
        return { code, message: messageFor(code), isRetry };
    });
}

/**
 * Read a `resolve_conflict` response.
 *
 * The RPC returns `{conflict_id, status, result?}` where `status` is the CONFLICT's
 * new status — and `'open'` is a legitimate, designed outcome: the reinject was
 * refused again and the row stayed in the queue with its new reason appended.
 *
 * @param {*} response
 * @returns {{resolved:boolean, status:string, reason:string|null, message:string}}
 */
export function readResolveResult(response) {
    const status = response && typeof response.status === 'string' ? response.status : '';
    const inner = response && response.result && typeof response.result === 'object'
        ? response.result
        : null;
    const reason = inner && typeof inner.reason === 'string' && inner.reason !== ''
        ? inner.reason
        : null;

    if (status === 'reinjected') {
        const innerStatus = inner && inner.status;
        return {
            resolved: true,
            status,
            reason: null,
            message: innerStatus === 'already_present'
                ? 'Réinjecté : cet enregistrement était déjà au registre (la première tentative avait bien abouti). '
                    + 'Aucun doublon n\'a été créé — l\'identifiant d\'origine a été rejoué.'
                : 'Réinjecté au registre avec son identifiant d\'origine. L\'argent est de nouveau compté.'
        };
    }

    if (status === 'rejected') {
        return {
            resolved: true,
            status,
            reason: null,
            message: 'Enregistrement rejeté : il n\'entrera pas au registre. '
                + 'Le motif saisi reste attaché à la décision dans le journal d\'audit.'
        };
    }

    // `open` — a re-conflict. NOT a dead end, and never reported as one.
    return {
        resolved: false,
        status: status || 'open',
        reason,
        message: 'Réinjection refusée à nouveau : ' + messageFor(reason)
            + ' L\'enregistrement RESTE dans la file avec ce nouveau motif — corrigez la cause, '
            + 'puis réessayez ; rejetez-le seulement si l\'argent ne doit pas entrer au registre.'
    };
}

/**
 * A one-line description of what the parked event WAS, for the queue list.
 *
 * Not escaped here — the caller escapes at the interpolation point, because the same
 * text is also written with `textContent` in places where escaping would double up.
 *
 * @param {object|null} event
 * @returns {{kindLabel:string, amount:number|null, patient:string|null, receipt:string|null}}
 */
export function describeEvent(event) {
    const e = event && typeof event === 'object' ? event : {};
    const kindLabel = KIND_LABEL[e.kind] || (e.kind ? String(e.kind) : 'Opération inconnue');
    const amount = Number.isInteger(e.amount) ? e.amount : null;
    const patient = typeof e.patient_name === 'string' && e.patient_name !== ''
        ? e.patient_name
        : null;
    const receipt = Number.isInteger(e.receipt_no)
        ? formatReceiptNo(e.receipt_no)
        : (e.receipt_no != null && e.receipt_no !== '' ? String(e.receipt_no) : null);
    return { kindLabel, amount, patient, receipt };
}

/** French names for the ledger kinds (spec §3.1). */
const KIND_LABEL = Object.freeze({
    sale: 'Vente',
    void: 'Annulation',
    discount: 'Remise',
    restock: 'Réapprovisionnement',
    stock_adjustment: 'Ajustement de stock'
});

// ---------------------------------------------------------------------------
// The screen
// ---------------------------------------------------------------------------

export const ConflictQueue = {
    elements: {},
    /** @type {QueueRow[]} The merged queue as last read. */
    rows: [],
    /** @type {Map<string, string>} device id → name */
    deviceNames: new Map(),
    /** @type {number|null} */
    countTimer: null,
    /** @type {(() => void)|null} */
    unsubscribe: null,
    /** @type {(() => void)|null} Held so `stop()` can detach it — a sign-in/out cycle
     *  would otherwise stack a new online listener every time. */
    onlineHandler: null,
    /** Whether resolved rows are shown. NEVER affects the count. */
    showResolved: false,

    init() {
        this.elements = {
            modal: document.getElementById('conflict-queue-modal'),
            openBtn: document.getElementById('manage-conflicts-btn'),
            dashboardBtn: document.getElementById('open-conflict-queue-btn'),
            closeBtn: document.getElementById('close-conflict-queue-modal'),
            refreshBtn: document.getElementById('refresh-conflicts-btn'),
            showResolved: document.getElementById('conflict-show-resolved'),
            tableBody: document.getElementById('conflicts-table-body'),
            notice: document.getElementById('conflict-queue-notice'),
            summary: document.getElementById('conflict-queue-summary'),
            banner: document.getElementById('conflict-queue-banner'),
            bannerMessage: document.getElementById('conflict-queue-banner-message'),
            cardCount: document.getElementById('conflicts-open-count'),
            cardTotal: document.getElementById('conflicts-open-total')
        };

        this.elements.openBtn?.addEventListener('click', () => void this.openModal());
        this.elements.dashboardBtn?.addEventListener('click', () => void this.openModal());
        this.elements.closeBtn?.addEventListener('click', () => this.closeModal());
        this.elements.refreshBtn?.addEventListener('click', () => void this.refresh());
        this.elements.modal?.addEventListener('click', (e) => {
            if (e.target === this.elements.modal) this.closeModal();
        });
        this.elements.showResolved?.addEventListener('change', () => {
            this.showResolved = !!this.elements.showResolved.checked;
            this.render();
        });

        // Delegated row actions — no conflict id is ever interpolated into an
        // attribute string; it is set with `setAttribute` on a real element.
        this.elements.tableBody?.addEventListener('click', (e) => {
            const button = e.target instanceof Element ? e.target.closest('button[data-action]') : null;
            if (!button) return;
            const eventId = button.getAttribute('data-event-id');
            const action = button.getAttribute('data-action');
            if (action === 'reinject') void this.resolve(eventId, 'reinject');
            else if (action === 'reject') void this.resolve(eventId, 'reject');
        });

        // The count must never be a number this module remembers. It is re-derived
        // from the tables on every engine status change, on a timer, and after every
        // resolution — a decremented cached counter is how a queue empties on screen
        // while the money is still parked.
        const engine = getEngine();
        if (engine && !this.unsubscribe) {
            this.unsubscribe = engine.onStatus(() => void this.refreshCount());
        }
        if (this.countTimer == null && typeof setInterval === 'function') {
            this.countTimer = setInterval(() => void this.refreshCount(), COUNT_REFRESH_MS);
        }
        if (typeof window !== 'undefined' && !this.onlineHandler) {
            this.onlineHandler = () => void this.refreshCount();
            window.addEventListener('online', this.onlineHandler);
        }

        void this.refreshCount();
        console.log('ConflictQueue module initialized');
    },

    async openModal() {
        this.elements.modal?.classList.remove('hidden');
        await this.refresh();
    },

    closeModal() {
        this.elements.modal?.classList.add('hidden');
    },

    // ---- reading -----------------------------------------------------------

    /** @returns {boolean} */
    isAdmin() {
        try {
            return !!(window.AppCore && window.AppCore.getCurrentRole
                && window.AppCore.getCurrentRole() === 'admin');
        } catch {
            return false;
        }
    },

    /** @returns {boolean} */
    isOnline() {
        return typeof navigator === 'undefined' ? true : navigator.onLine !== false;
    },

    /**
     * Read the queue from both sides.
     *
     * The local mirror is read ALWAYS, even for an admin with a live server: it holds
     * this browser's own parked events, and a device that cannot reach the server is
     * exactly the device most likely to be holding one.
     *
     * @returns {Promise<{rows:QueueRow[], serverRead:boolean, error:Error|null}>}
     */
    async readQueue() {
        /** @type {object[]} */
        let localRows = [];
        try {
            localRows = await db.table('conflicts').toArray();
        } catch (error) {
            console.warn('Miroir local des conflits illisible:', error);
        }

        let serverRows = [];
        let serverRead = false;
        /** @type {Error|null} */
        let error = null;

        const engine = getEngine();
        if (engine && engine.transport && this.isOnline()) {
            try {
                // `status: null` reads every status; the COUNT still filters to `open`.
                // Reading only the open rows would make a resolved row disappear from
                // the list the instant it was resolved, with no confirmation that the
                // decision landed.
                serverRows = await engine.transport.fetchConflicts({ status: null });
                serverRead = true;
            } catch (e) {
                error = /** @type {Error} */ (e);
                console.warn('Lecture des conflits serveur impossible:', e);
            }
        }

        return { rows: mergeConflicts(localRows, serverRows), serverRead, error };
    },

    /** Device names, so a row says « Caisse A — portable » and not a bare UUID. */
    async loadDeviceNames() {
        try {
            const devices = await fetchAllPages((from, to) => supabase
                .from('devices')
                .select('id, name')
                .order('id', { ascending: true })
                .range(from, to));
            this.deviceNames = new Map(devices.map(d => [d.id, d.name]));
        } catch (error) {
            console.warn('Noms des appareils indisponibles:', error);
        }
    },

    /**
     * Re-derive the count and repaint every surface that shows it.
     *
     * Called on init, on every engine status change, on a timer, on `online`, and
     * after each resolution — so the dashboard figure is never older than the last
     * thing that could have changed it.
     */
    async refreshCount() {
        try {
            const { rows, serverRead, error } = await this.readQueue();
            this.rows = rows;
            this.paintCount(rows);
            // If the list is on screen it must not disagree with the count next to it:
            // a row resolved from another browser has to leave the table too.
            const modal = this.elements.modal;
            if (modal && !modal.classList.contains('hidden')) this.render(serverRead, error);
        } catch (error) {
            console.warn('Comptage des conflits impossible:', error);
        }
    },

    /** Full read + repaint of the modal. */
    async refresh() {
        const body = this.elements.tableBody;
        if (body) {
            body.innerHTML =
                '<tr><td colspan="7" class="text-center text-muted">Lecture de la file…</td></tr>';
        }

        // A push may be sitting in the outbox behind a network blip; kicking first
        // means the queue the admin reads is the freshest one available.
        const engine = getEngine();
        if (engine && this.isOnline()) {
            try { await engine.kick(); } catch { /* the read below still stands */ }
        }

        await this.loadDeviceNames();
        const { rows, serverRead, error } = await this.readQueue();
        this.rows = rows;
        this.paintCount(rows);
        this.render(serverRead, error);
    },

    // ---- painting ----------------------------------------------------------

    /**
     * @param {QueueRow[]} rows
     */
    paintCount(rows) {
        const open = openConflicts(rows);
        const count = open.length;
        const total = open.reduce(
            (sum, r) => sum + (r.event && Number.isInteger(r.event.amount) ? r.event.amount : 0),
            0
        );

        if (this.elements.cardCount) this.elements.cardCount.textContent = String(count);
        if (this.elements.cardTotal) this.elements.cardTotal.textContent = formatCurrency(total);

        // The dashboard banner. Money parked in a conflict is money the hospital took,
        // so it is visible without opening any screen.
        if (this.elements.banner) {
            this.elements.banner.classList.toggle('hidden', count === 0);
        }
        if (this.elements.bannerMessage) {
            this.elements.bannerMessage.textContent = count === 0
                ? ''
                : `${conflictQueueNotice(count)} — ${formatCurrency(total)} encaissé(s) `
                    + 'et refusé(s) par le serveur. Tant qu\'ils sont ici, cet argent n\'est dans aucun total.';
        }
    },

    /**
     * @param {boolean} [serverRead]
     * @param {Error|null} [error]
     */
    render(serverRead = true, error = null) {
        this.renderNotice(serverRead, error);
        this.renderSummary();

        const body = this.elements.tableBody;
        if (!body) return;
        body.innerHTML = '';

        const visible = this.showResolved ? this.rows : openConflicts(this.rows);
        if (visible.length === 0) {
            body.innerHTML = '<tr><td colspan="7" class="text-center text-muted">'
                + (this.showResolved
                    ? 'Aucun enregistrement refusé, ni en attente ni résolu.'
                    : 'Aucun enregistrement en attente de décision. La file est vide.')
                + '</td></tr>';
            return;
        }

        for (const row of visible) {
            body.appendChild(this.renderRow(row));
        }
    },

    /**
     * @param {QueueRow} row
     * @returns {HTMLTableRowElement}
     */
    renderRow(row) {
        const what = describeEvent(row.event);
        const segments = reasonSegments(row.reason);
        const deviceLabel = row.deviceId
            ? (this.deviceNames.get(row.deviceId) || `appareil inconnu (${row.deviceId.slice(0, 8)}…)`)
            : 'aucun appareil — cet enregistrement n\'apparaît dans la file d\'aucun poste';

        const statusBadge = row.status === 'open'
            ? '<span class="badge badge-danger">En attente de décision</span>'
            : row.status === 'reinjected'
                ? '<span class="badge badge-success">Réinjecté</span>'
                : '<span class="badge badge-warning">Rejeté</span>';

        const retryCount = segments.filter(s => s.isRetry).length;
        const retryNote = retryCount > 0
            ? `<div class="text-xs" style="color: var(--danger-600);">`
                + `${retryCount} réinjection(s) déjà refusée(s) — toujours dans la file, jamais une impasse.`
                + '</div>'
            : '';

        const tr = document.createElement('tr');
        tr.innerHTML = `
            <td>
                <div class="font-medium">${escapeHtml(what.kindLabel)}</div>
                <div class="text-xs text-muted"><code>${escapeHtml(row.eventId)}</code></div>
            </td>
            <td class="font-medium">${what.amount == null
                ? '<span class="text-muted">montant illisible</span>'
                : escapeHtml(formatCurrency(what.amount))}</td>
            <td>${what.patient == null
                ? '<span class="text-muted">—</span>'
                : escapeHtml(what.patient)}</td>
            <td>${what.receipt == null
                ? '<span class="text-muted">—</span>'
                : `<code>${escapeHtml(what.receipt)}</code>`}</td>
            <td class="text-sm">
                ${segments.map(s => `<div>${s.isRetry ? '<strong>Après réinjection : </strong>' : ''}`
                    + `${escapeHtml(s.message)}</div>`).join('')}
                ${retryNote}
            </td>
            <td class="text-sm">
                <div>${escapeHtml(row.raisedAt ? formatDateTime(row.raisedAt) : 'date inconnue')}</div>
                <div class="text-xs text-muted">${escapeHtml(deviceLabel)}</div>
                <div class="text-xs text-muted">${statusBadge}</div>
            </td>
            <td class="text-center"></td>`;

        const actions = tr.lastElementChild;
        if (row.status !== 'open') {
            const done = document.createElement('span');
            done.className = 'text-xs text-muted';
            done.textContent = 'Décision prise.';
            actions.appendChild(done);
            return tr;
        }

        if (!row.conflictId) {
            // The park is known to this device but the server row has not reached us.
            // Offering a button that cannot possibly work is worse than saying so.
            const wait = document.createElement('span');
            wait.className = 'text-xs';
            wait.style.color = 'var(--danger-600)';
            wait.textContent = 'En attente de synchronisation : la décision se prend en ligne, '
                + 'une fois la fiche du serveur reçue.';
            actions.appendChild(wait);
            return tr;
        }

        actions.appendChild(this.actionButton(
            'reinject', row.eventId, 'Réinjecter', 'btn btn-success btn-sm'));
        actions.appendChild(this.actionButton(
            'reject', row.eventId, 'Rejeter', 'btn btn-danger btn-sm'));
        return tr;
    },

    /**
     * @param {string} action
     * @param {string} eventId
     * @param {string} label
     * @param {string} className
     * @returns {HTMLButtonElement}
     */
    actionButton(action, eventId, label, className) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = className;
        button.textContent = label;
        button.setAttribute('data-action', action);
        button.setAttribute('data-event-id', eventId);
        return button;
    },

    /**
     * @param {boolean} serverRead
     * @param {Error|null} error
     */
    renderNotice(serverRead, error) {
        const el = this.elements.notice;
        if (!el) return;
        const parts = [];
        if (!serverRead) {
            parts.push('File lue depuis cet appareil uniquement — le serveur est injoignable. '
                + 'Les enregistrements refusés d\'autres postes ne sont pas affichés, '
                + 'et aucune décision ne peut être prise hors ligne.');
        }
        if (error) parts.push(messageForError(error));
        if (!this.isAdmin()) {
            parts.push('Seul un administrateur peut réinjecter ou rejeter un enregistrement.');
        }
        el.textContent = parts.join(' ');
        el.classList.toggle('hidden', parts.length === 0);
    },

    renderSummary() {
        const el = this.elements.summary;
        if (!el) return;
        const open = openConflicts(this.rows);
        const total = open.reduce(
            (sum, r) => sum + (r.event && Number.isInteger(r.event.amount) ? r.event.amount : 0),
            0
        );
        el.textContent = open.length === 0
            ? 'Aucun enregistrement en attente de décision.'
            : `${conflictQueueNotice(open.length)} — ${formatCurrency(total)} au total. `
                + 'Cet argent a été encaissé mais n\'est dans aucun total tant qu\'il est ici.';
    },

    // ---- writing -----------------------------------------------------------

    /**
     * Reinject or reject one parked event.
     *
     * REINJECT replays the ORIGINAL UUID as the ORIGINAL caller (migration 0005), so
     * re-running is idempotent by construction: an event that did land the first time
     * heals to `already_present` and no duplicate is created. That is why this button
     * is safe to press twice, and why it is offered at all.
     *
     * @param {string|null} eventId
     * @param {'reinject'|'reject'} action
     */
    async resolve(eventId, action) {
        if (!eventId) return;
        const row = this.rows.find(r => r.eventId === eventId);
        if (!row) {
            showStatus('Enregistrement introuvable dans la file — rechargez-la.', true);
            return;
        }
        if (row.status !== 'open') {
            showStatus('Cet enregistrement a déjà été traité.', true);
            return;
        }
        if (!row.conflictId) {
            showStatus('La fiche serveur de cet enregistrement n\'est pas encore parvenue à cet appareil. '
                + 'Synchronisez, puis réessayez.', true);
            return;
        }
        if (!this.isAdmin()) {
            showStatus(messageFor('caller-not-admin'), true);
            return;
        }

        const engine = getEngine();
        if (!engine || !engine.transport) {
            showStatus(messageFor('engine-not-ready'), true);
            return;
        }
        if (!this.isOnline()) {
            showStatus('La résolution d\'un conflit exige une connexion au serveur. '
                + 'Rétablissez le réseau, puis recommencez.', true);
            return;
        }

        const what = describeEvent(row.event);
        const amountText = what.amount == null ? 'montant illisible' : formatCurrency(what.amount);

        /** @type {string|null} */
        let note = null;
        if (action === 'reject') {
            // A rejection says money the hospital took will NEVER enter the ledger.
            // It is the only irreversible button on this screen, so it costs a reason —
            // audit M3 is a void reason collected and thrown away, and this is the same
            // decision with the same need for a name attached.
            note = prompt(
                `REJETER définitivement cet enregistrement ?\n\n`
                + `${what.kindLabel} — ${amountText}${what.receipt ? ` — reçu ${what.receipt}` : ''}\n\n`
                + 'Il n\'entrera JAMAIS au registre. Si cet argent a réellement été encaissé, '
                + 'réinjectez-le plutôt.\n\nMotif du rejet (obligatoire) :');
            if (note === null) return;
            if (note.trim() === '') {
                showStatus('Un motif est obligatoire pour rejeter un enregistrement.', true);
                return;
            }
            note = note.trim();
        } else {
            const confirmed = confirm(
                `Réinjecter cet enregistrement au registre ?\n\n`
                + `${what.kindLabel} — ${amountText}${what.receipt ? ` — reçu ${what.receipt}` : ''}\n\n`
                + 'Il est rejoué avec son identifiant d\'origine : s\'il avait finalement abouti, '
                + 'aucun doublon ne sera créé.');
            if (!confirmed) return;
            const typed = prompt('Note (facultative) :', '');
            note = typed === null ? null : (typed.trim() || null);
        }

        this.setRowBusy(eventId, true);
        try {
            showStatus(action === 'reinject' ? 'Réinjection…' : 'Rejet…', false);
            const response = await engine.transport.resolveConflict({
                conflictId: row.conflictId,
                action,
                note
            });
            const outcome = readResolveResult(response);

            // Pull first, so the local mirror learns the new status (and, on a
            // successful reinject, receives the event itself) before we repaint.
            try { await engine.kick(); } catch { /* the server read below still stands */ }
            await this.refresh();

            showStatus(outcome.message, !outcome.resolved);
        } catch (error) {
            console.error('Résolution du conflit refusée:', error);
            showStatus(error && error.code === '42501'
                ? messageFor('caller-not-admin')
                : messageForError(error), true);
        } finally {
            this.setRowBusy(eventId, false);
        }
    },

    /**
     * Disable a row's buttons while its decision is in flight — the same
     * disable-before-the-await discipline the audit credits the cashier forms for.
     * @param {string} eventId
     * @param {boolean} busy
     */
    setRowBusy(eventId, busy) {
        const body = this.elements.tableBody;
        if (!body) return;
        for (const button of body.querySelectorAll('button[data-event-id]')) {
            if (button.getAttribute('data-event-id') === eventId) {
                /** @type {HTMLButtonElement} */ (button).disabled = busy;
            }
        }
    },

    /** Stop the count timer (sign-out). */
    stop() {
        if (this.countTimer != null) {
            clearInterval(this.countTimer);
            this.countTimer = null;
        }
        if (this.unsubscribe) {
            this.unsubscribe();
            this.unsubscribe = null;
        }
        if (this.onlineHandler && typeof window !== 'undefined') {
            window.removeEventListener('online', this.onlineHandler);
            this.onlineHandler = null;
        }
    }
};

// Guarded, because the PURE half of this module (the merge, the open-only count, the
// reason parsing, the reinject-outcome reading) is unit-tested in Node, where there is
// no `window`. An unguarded assignment would throw at import.
if (typeof window !== 'undefined') window.ConflictQueue = ConflictQueue;
